import { jsPDF } from 'jspdf'
import type { AssessmentReportRecord } from '@really-assess/shared/integrations'

export interface AssessmentReportPdfInput {
  assessmentTitle: string
  learnerName: string
  dateTaken: string
  integrityScore: number
  passed: boolean
  report: AssessmentReportRecord
}

const MARGIN_MM = 18
const PAGE_WIDTH = 210
const PAGE_HEIGHT = 297
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_MM * 2

function wrapText(doc: jsPDF, text: string, maxWidth: number, fontSize: number): string[] {
  doc.setFontSize(fontSize)
  return doc.splitTextToSize(text.replace(/\s+/g, ' ').trim(), maxWidth) as string[]
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed <= PAGE_HEIGHT - MARGIN_MM) return y
  doc.addPage()
  return MARGIN_MM
}

function drawFooter(doc: jsPDF, pageNumber: number, totalPages: number) {
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(120, 120, 120)
  doc.text('ReallyAssess', MARGIN_MM, PAGE_HEIGHT - 10)
  doc.text(`Page ${pageNumber} of ${totalPages}`, PAGE_WIDTH - MARGIN_MM, PAGE_HEIGHT - 10, {
    align: 'right',
  })
}

function drawSection(
  doc: jsPDF,
  title: string,
  body: string,
  startY: number
): number {
  let y = ensureSpace(doc, startY, 28)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.setTextColor(25, 25, 25)
  doc.text(title, MARGIN_MM, y)
  y += 3

  doc.setDrawColor(200, 200, 200)
  doc.setLineWidth(0.3)
  doc.line(MARGIN_MM, y, PAGE_WIDTH - MARGIN_MM, y)
  y += 7

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(45, 45, 45)

  const lines = wrapText(doc, body, CONTENT_WIDTH, 10)
  const lineHeight = 5.2

  for (const line of lines) {
    y = ensureSpace(doc, y, lineHeight + 2)
    doc.text(line, MARGIN_MM, y)
    y += lineHeight
  }

  return y + 8
}

/**
 * Build a clean, print-oriented assessment report PDF (not a screenshot).
 */
export function buildAssessmentReportPdf(input: AssessmentReportPdfInput): jsPDF {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  let y = MARGIN_MM

  // Cover / header
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.setTextColor(20, 20, 20)
  doc.text(input.assessmentTitle || 'Assessment Report', MARGIN_MM, y)
  y += 8

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(80, 80, 80)
  doc.text('Assessment Report', MARGIN_MM, y)
  y += 10

  doc.setDrawColor(180, 180, 180)
  doc.setLineWidth(0.4)
  doc.line(MARGIN_MM, y, PAGE_WIDTH - MARGIN_MM, y)
  y += 8

  const meta: Array<[string, string]> = [
    ['Learner', input.learnerName],
    ['Date', input.dateTaken],
    ['Integrity score', `${input.integrityScore}%`],
    ['Result', input.passed ? 'Passed' : 'Not passed'],
  ]

  doc.setFontSize(10)
  for (const [label, value] of meta) {
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(60, 60, 60)
    doc.text(`${label}:`, MARGIN_MM, y)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(30, 30, 30)
    doc.text(value, MARGIN_MM + 38, y)
    y += 6
  }

  y += 8

  y = drawSection(doc, 'Overall Competency', input.report.overallCompetency, y)
  y = drawSection(doc, 'The Why', input.report.theWhy, y)
  drawSection(doc, 'TSC Breakdown', input.report.tscBreakdown, y)

  const totalPages = doc.getNumberOfPages()
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page)
    drawFooter(doc, page, totalPages)
  }

  return doc
}

export function downloadAssessmentReportPdf(input: AssessmentReportPdfInput): void {
  const doc = buildAssessmentReportPdf(input)
  const safeName = input.learnerName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '') || 'learner'
  doc.save(`ReallyAssess_Report_${safeName}.pdf`)
}
